import * as React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import makeStyles from '@material-ui/core/styles/makeStyles'
import Media from '@oakwood/oui/Media'
import MediaReveal from '@oakwood/oui/MediaReveal'
import { mediaType } from 'utils'
import MuiAccordion from 'components/Accordion'
import MuiAccordionDetails from 'components/AccordionDetails'
import MuiAccordionSummary from 'components/AccordionSummary'
import AddIcon from 'components/icons/Add'
import Container from 'components/Container'
import Section from 'components/Section'
import Typography from 'components/Typography'
import Html from 'components/Html'

const BREAKPOINT_KEY_UP = 'sm'

export const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: -1,
    borderTop: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.up(BREAKPOINT_KEY_UP)]: {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  backgroundMediaContainer: {
    ...theme.mixins.absolute(0),
    zIndex: -1,
    [theme.breakpoints.up(BREAKPOINT_KEY_UP)]: {
      right: '50%',
      width: '50%',
    },
  },
  backgroundMedia: {
    height: '100%',
  },
  main: {
    position: 'static',
    display: 'grid',
    [theme.breakpoints.up(BREAKPOINT_KEY_UP)]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
  },
  area: {
    [theme.breakpoints.up(BREAKPOINT_KEY_UP)]: {
      minHeight: 400,
    },
  },
  contentArea: {
    position: 'relative',
    padding: 'var(--coa-section-spacing) var(--coa-container-spacing)',
    [theme.breakpoints.up(BREAKPOINT_KEY_UP)]: {
      position: 'static',
    },
  },
  accordionArea: {
    '& a': {
      color: 'inherit',
    },
    [theme.breakpoints.up(BREAKPOINT_KEY_UP)]: {
      padding: 'var(--coa-section-spacing) var(--coa-container-spacing)',
    },
  },
  text: {
    paddingRight: theme.spacing(4),
  },
  accordionExpandIcon: {
    '[class*="expanded"] &': {
      transform: 'none',
      '& path.plus': {
        display: 'none',
      },
    },
  },
}))

function Accordion(props) {
  const { backgroundMediaProps, items, renderIndex, text } = props
  const classes = useStyles(props)

  const [expanded, setExpanded] = React.useState(false)
  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false)
  }

  return (
    <Section className={classes.root}>
      <Container className={classes.main} disableGutters>
        <div className={clsx(classes.area, classes.contentArea)}>
          {backgroundMediaProps && (
            <MediaReveal className={classes.backgroundMediaContainer}>
              <Media className={classes.backgroundMedia} {...backgroundMediaProps} />
            </MediaReveal>
          )}

          {text && <Html className={classes.text} dangerouslySetInnerHTML={{ __html: text }} />}
        </div>

        <div className={clsx(classes.area, classes.accordionArea)}>
          {items?.map((item, idx) => {
            const id = `coa-block-accordion-${renderIndex}-${idx}`

            return (
              <React.Fragment key={idx}>
                {item.summary && (
                  <MuiAccordion expanded={expanded === id} onChange={handleChange(id)}>
                    <MuiAccordionSummary
                      classes={{ expandIcon: classes.accordionExpandIcon }}
                      expandIcon={<AddIcon fontSize="small" />}
                      aria-controls={`${id}-content`}
                      id={`${id}-header`}
                    >
                      <Typography variant="button">{item.summary}</Typography>
                    </MuiAccordionSummary>

                    <MuiAccordionDetails>
                      <Typography
                        component="div"
                        dangerouslySetInnerHTML={{ __html: item.details }}
                      />
                    </MuiAccordionDetails>
                  </MuiAccordion>
                )}
              </React.Fragment>
            )
          })}
        </div>
      </Container>
    </Section>
  )
}

const itemType = PropTypes.shape({
  details: PropTypes.string.isRequired,
  summary: PropTypes.string.isRequired,
})

Accordion.propTypes = {
  backgroundMediaProps: mediaType,
  items: PropTypes.arrayOf(itemType).isRequired,
  renderIndex: PropTypes.number,
  text: PropTypes.string.isRequired,
}

export default Accordion
